import React, { useEffect, useRef, useState, useCallback } from "react";
import QRCode from "qrcode";
import { X, Smartphone, RefreshCw, CheckCircle2, Copy, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * PhotoBridgeModal — Desktop-Brücke: QR-Code, der den User auf eine Mobile-
 * Upload-Seite leitet. Beim Öffnen erzeugt das Backend ein Bridge-Token,
 * wir polling alle 2.5s nach `upload_count` und triggern `onPhotosArrived`
 * sobald neue Fotos angekommen sind. Schließt sich automatisch wenn Token
 * abläuft oder explizit widerrufen wird.
 *
 * Props:
 *   - caseId: aktuelle Akten-ID
 *   - open: bool — sichtbar
 *   - onClose: Callback
 *   - onPhotosArrived: Callback nach erfolgreichen Bridge-Uploads (lädt Akte neu)
 */
export default function PhotoBridgeModal({ caseId, open, onClose, onPhotosArrived }) {
  const [bridge, setBridge] = useState(null);    // { token, upload_path, expires_at, upload_count }
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const lastCountRef = useRef(0);
  const pollRef = useRef(null);

  const fullUrl = bridge ? `${window.location.origin}${bridge.upload_path}` : "";

  const create = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/photo-bridge`);
      setBridge(data);
      lastCountRef.current = data.upload_count || 0;
    } catch (e) {
      toast.error(apiError(e) || "Konnte Mobile-Upload-Link nicht erzeugen");
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [caseId, onClose]);

  // Beim Öffnen: Bridge anlegen + QR rendern. Beim Schließen: Polling stoppen.
  useEffect(() => {
    if (open && caseId) {
      create();
    } else {
      setBridge(null);
      setQrDataUrl("");
    }
  }, [open, caseId, create]);

  // QR-Code rendern sobald wir eine URL haben
  useEffect(() => {
    if (!fullUrl) { setQrDataUrl(""); return; }
    QRCode.toDataURL(fullUrl, {
      width: 320,
      margin: 1,
      color: { dark: "#0f172a", light: "#ffffff" },
      errorCorrectionLevel: "M",
    }).then(setQrDataUrl).catch(() => setQrDataUrl(""));
  }, [fullUrl]);

  // Polling für neue Uploads
  useEffect(() => {
    if (!open || !bridge?.token) return;
    const poll = async () => {
      try {
        const { data } = await api.get(`/cases/${caseId}/photo-bridge/status`);
        if (!data.active) return;
        if (data.upload_count > lastCountRef.current) {
          const newCount = data.upload_count - lastCountRef.current;
          lastCountRef.current = data.upload_count;
          setBridge((b) => b ? { ...b, upload_count: data.upload_count } : b);
          toast.success(`${newCount} neue${newCount === 1 ? "s" : ""} Foto${newCount === 1 ? "" : "s"} vom Handy empfangen`);
          onPhotosArrived?.();
        }
      } catch { /* ignore */ }
    };
    pollRef.current = setInterval(poll, 2500);
    return () => clearInterval(pollRef.current);
  }, [open, bridge?.token, caseId, onPhotosArrived]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Konnte Link nicht kopieren");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="photo-bridge-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Smartphone className="h-5 w-5 text-white" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-display font-semibold text-zinc-900">Vom Handy hochladen</div>
              <div className="text-xs text-zinc-500">QR-Code mit dem Handy scannen</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center"
            data-testid="photo-bridge-close"
            aria-label="Schließen"
          >
            <X className="h-5 w-5 text-zinc-600" />
          </button>
        </div>

        <div className="p-6 flex flex-col items-center gap-4">
          {loading || !qrDataUrl ? (
            <div className="h-[320px] w-[320px] flex items-center justify-center bg-zinc-50 rounded-xl">
              <RefreshCw className="h-8 w-8 text-zinc-400 animate-spin" />
            </div>
          ) : (
            <div className="p-3 bg-white rounded-xl border-2 border-zinc-100 shadow-sm">
              <img src={qrDataUrl} alt="QR-Code" width={320} height={320} data-testid="photo-bridge-qr" />
            </div>
          )}

          {bridge && (
            <>
              <div className="text-center">
                <div className="text-sm font-medium text-zinc-700">1. Handy-Kamera öffnen</div>
                <div className="text-sm font-medium text-zinc-700">2. QR-Code anvisieren</div>
                <div className="text-sm font-medium text-zinc-700">3. Auf den Link tippen</div>
              </div>

              <button
                onClick={copyLink}
                className="w-full text-xs text-zinc-500 hover:text-zinc-900 flex items-center justify-center gap-1.5 py-2 border-t border-zinc-100"
                data-testid="photo-bridge-copy"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                <span className="font-mono truncate">{fullUrl.replace(/^https?:\/\//, "")}</span>
              </button>

              {bridge.upload_count > 0 && (
                <div
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-emerald-50 border border-emerald-200 rounded-lg"
                  data-testid="photo-bridge-counter"
                >
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-semibold text-emerald-900">
                    {bridge.upload_count} {bridge.upload_count === 1 ? "Foto" : "Fotos"} empfangen
                  </span>
                </div>
              )}

              <div className="text-[11px] text-zinc-400 text-center">
                Link bleibt ~4 Stunden aktiv · Max. {bridge.max_uploads} Uploads
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
